import { createContext, PropsWithChildren, useContext, useEffect, useMemo } from 'react';

import { filterVideosByAwaiting } from '../group-connection-request';
import type { GroupConnectionRequestPayload } from '../group-connection-request/types';

import { getMeetingParticipants, getParticipantDisplayNames } from './meet.adapters';
import { useMeetGroupFlow } from './useMeetGroupFlow';
import type { GroupConnectionRequestViewModel, MeetingParticipantViewModel, RemoteVideoLike } from './types';

export interface MeetModerationBridgeApi {
  canManageParticipants: boolean;
  onGroupConnectionRequest: (payload: GroupConnectionRequestPayload) => void;
  handleConnectionAllowed: (identity?: string) => void;
  handleParticipantLeft: (identity: string) => void;
  handleRaisedHandChange: (payload: { identity: string; raised: boolean }) => void;
  toggleRaisedHand: () => void;
  setIsCoOrganizer: (value: boolean) => void;
  remoteVideosForMeeting: RemoteVideoLike[];
}

interface MeetModerationProviderProps {
  roomName: string;
  isGroupConsultationFlag: boolean;
  isPsychologist: boolean;
  localParticipantIdentity?: string;
  sendConnectionAllowed?: (id: string) => void;
  remoteVideos: RemoteVideoLike[];
  psychologistId?: number | null;
  onParticipantNamesChange?: (names: Record<string, string>) => void;
  onApi?: (api: MeetModerationBridgeApi) => void;
}

interface MeetModerationContextValue {
  isGroupConsultationFlag: boolean;
  canManageParticipants: boolean;
  isCoOrganizer: boolean;
  isLocalHandRaised: boolean;
  awaitingCount: number;
  groupParticipantsPanelOpen: boolean;
  visibleConnectionRequests: GroupConnectionRequestViewModel[];
  visibleAwaitingParticipants: GroupConnectionRequestViewModel[];
  groupPermissionRequested: boolean;
  connectionAllowedForGroupClient: boolean;
  raisedHandParticipantIds: Set<string>;
  remoteVideosForMeeting: RemoteVideoLike[];
  meetingParticipants: MeetingParticipantViewModel[];
  requestGroupPermission: (displayName: string) => void;
  onGroupConnectionRequest: (payload: GroupConnectionRequestPayload) => void;
  handleConnectionAllowed: (identity?: string) => void;
  handleRaisedHandChange: (payload: { identity: string; raised: boolean }) => void;
  handleAllowConnectionRequest: (id: string) => void;
  dismissConnectionRequest: (id: string) => void;
  hideAwaitingParticipant: (id: string) => void;
  allowAllConnections: () => void;
  toggleRaisedHand: () => void;
  setGroupParticipantsPanelOpen: (value: boolean) => void;
}

const MeetModerationContext = createContext<MeetModerationContextValue | null>(null);

export function MeetModerationProvider({
  children,
  roomName,
  isGroupConsultationFlag,
  isPsychologist,
  localParticipantIdentity,
  sendConnectionAllowed,
  remoteVideos,
  psychologistId,
  onParticipantNamesChange,
  onApi,
}: PropsWithChildren<MeetModerationProviderProps>) {
  // Считаем по исходному списку, а не по remoteVideosForMeeting: тот отфильтрован
  // по очереди ожидания, поэтому подключившегося участника в нём ещё нет.
  const presentParticipantIds = useMemo(
    () => remoteVideos.map((video) => String(video.identity ?? '')).filter(Boolean),
    [remoteVideos],
  );

  const moderation = useMeetGroupFlow({
    roomName,
    isGroupConsultationFlag,
    isPsychologist,
    localParticipantIdentity,
    presentParticipantIds,
    sendConnectionAllowed,
  });

  const remoteVideosForMeeting = useMemo(() => {
    const list =
      !(isGroupConsultationFlag && moderation.canManageParticipants) || moderation.awaitingList.length === 0
        ? remoteVideos
        : filterVideosByAwaiting(
            remoteVideos,
            moderation.awaitingList.map((participant) => participant.identity),
          );

    return [...list].sort((left, right) => {
      const leftId = String(left.identity ?? left.display ?? '');
      const rightId = String(right.identity ?? right.display ?? '');
      return leftId.localeCompare(rightId);
    });
  }, [isGroupConsultationFlag, moderation.awaitingList, moderation.canManageParticipants, remoteVideos]);

  const meetingParticipants = useMemo(
    () => getMeetingParticipants(remoteVideosForMeeting, psychologistId),
    [psychologistId, remoteVideosForMeeting],
  );

  useEffect(() => {
    if (!isGroupConsultationFlag || remoteVideosForMeeting.length === 0) {
      onParticipantNamesChange?.({});
      return;
    }

    onParticipantNamesChange?.(getParticipantDisplayNames(remoteVideosForMeeting));
  }, [isGroupConsultationFlag, onParticipantNamesChange, remoteVideosForMeeting]);

  useEffect(() => {
    if (!isGroupConsultationFlag) {
      moderation.syncRaisedHands([]);
      return;
    }

    const participantIds = remoteVideosForMeeting
      .map((video) => String(video.identity ?? ''))
      .filter(Boolean);

    if (localParticipantIdentity) {
      participantIds.push(localParticipantIdentity);
    }

    moderation.syncRaisedHands(participantIds);
  }, [isGroupConsultationFlag, localParticipantIdentity, moderation.syncRaisedHands, remoteVideosForMeeting]);

  useEffect(() => {
    onApi?.({
      canManageParticipants: moderation.canManageParticipants,
      onGroupConnectionRequest: moderation.onGroupConnectionRequest,
      handleConnectionAllowed: moderation.handleConnectionAllowed,
      handleParticipantLeft: moderation.handleParticipantLeft,
      handleRaisedHandChange: moderation.handleRaisedHandChange,
      toggleRaisedHand: moderation.toggleRaisedHand,
      setIsCoOrganizer: moderation.setIsCoOrganizer,
      remoteVideosForMeeting,
    });
  }, [
    moderation.canManageParticipants,
    moderation.handleConnectionAllowed,
    moderation.handleParticipantLeft,
    moderation.handleRaisedHandChange,
    moderation.onGroupConnectionRequest,
    moderation.toggleRaisedHand,
    moderation.setIsCoOrganizer,
    onApi,
    remoteVideosForMeeting,
  ]);

  const value = useMemo<MeetModerationContextValue>(() => ({
    isGroupConsultationFlag,
    canManageParticipants: moderation.canManageParticipants,
    isCoOrganizer: moderation.isCoOrganizer,
    isLocalHandRaised: moderation.isLocalHandRaised,
    awaitingCount: moderation.awaitingFromDataChannel,
    groupParticipantsPanelOpen: moderation.groupParticipantsPanelOpen,
    visibleConnectionRequests: moderation.visibleConnectionRequests,
    visibleAwaitingParticipants: moderation.visibleAwaitingParticipants,
    groupPermissionRequested: moderation.groupPermissionRequested,
    connectionAllowedForGroupClient: moderation.connectionAllowedForGroupClient,
    raisedHandParticipantIds: moderation.raisedHandParticipantIds,
    remoteVideosForMeeting,
    meetingParticipants,
    requestGroupPermission: moderation.requestGroupPermission,
    onGroupConnectionRequest: moderation.onGroupConnectionRequest,
    handleConnectionAllowed: moderation.handleConnectionAllowed,
    handleRaisedHandChange: moderation.handleRaisedHandChange,
    handleAllowConnectionRequest: moderation.handleAllowConnectionRequest,
    dismissConnectionRequest: moderation.dismissConnectionRequest,
    hideAwaitingParticipant: moderation.hideAwaitingParticipant,
    allowAllConnections: moderation.allowAllConnections,
    toggleRaisedHand: moderation.toggleRaisedHand,
    setGroupParticipantsPanelOpen: moderation.setGroupParticipantsPanelOpen,
  }), [
    isGroupConsultationFlag,
    meetingParticipants,
    moderation.allowAllConnections,
    moderation.canManageParticipants,
    moderation.connectionAllowedForGroupClient,
    moderation.dismissConnectionRequest,
    moderation.groupParticipantsPanelOpen,
    moderation.groupPermissionRequested,
    moderation.handleAllowConnectionRequest,
    moderation.handleConnectionAllowed,
    moderation.handleRaisedHandChange,
    moderation.hideAwaitingParticipant,
    moderation.isLocalHandRaised,
    moderation.isCoOrganizer,
    moderation.onGroupConnectionRequest,
    moderation.raisedHandParticipantIds,
    moderation.requestGroupPermission,
    moderation.setGroupParticipantsPanelOpen,
    moderation.toggleRaisedHand,
    moderation.visibleAwaitingParticipants,
    moderation.visibleConnectionRequests,
    moderation.awaitingFromDataChannel,
    remoteVideosForMeeting,
  ]);

  return <MeetModerationContext.Provider value={value}>{children}</MeetModerationContext.Provider>;
}

export function useMeetModeration() {
  const context = useContext(MeetModerationContext);

  if (!context) {
    throw new Error('useMeetModeration must be used within MeetModerationProvider');
  }

  return context;
}
